import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import BasePinController from "./BasePinController";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";
import NtTabController from "../../components/NtTabController";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtModal from "../../components/NtModal";
import NtText from "../../components/NtText";
import NtBadge from "../../components/NtBadge";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtCallingRatesItem from "../components/NtCallingRatesItem";
import NtCallingRatesStatItem from "../components/NtCallingRatesStatItem";
import NtCallingRatesTariffNewForm from "../components/NtCallingRatesTariffNewForm";
import { useTheme } from "../../context/ThemeContext";
import callingratescolumndata from "../data/callingratescolumndata";
import { fetchCallingRates, fetchTariffs } from "../../api/billing";
import {
  asyncStorageSetData,
  parseDecimalPlaces,
  parseSimpleDisplayDate,
  showToast,
} from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import { fetchCurrency } from "../utils/CurrencyParser";

// Hero strip at the top of the page — tariff identity + headline numbers.
// Function component so it can read `theme` (the surrounding controller is a
// class).
function TariffHero({ tariff }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeHeroStyles(theme), [theme]);
  if (!tariff) return null;
  const currency = tariff.currency;
  const symbol = currency?.symbol || "";

  return (
    <View style={styles.hero}>
      <View style={styles.accent} />
      <View style={styles.heroBody}>
        <View style={styles.heroHeader}>
          <View style={styles.heroIdRow}>
            <NtText style={styles.label}>Tariff</NtText>
            <NtBadge variant="info" dot={false}>
              {tariff.id}
            </NtBadge>
            {currency ? (
              <View style={styles.currencyChip}>
                <NtMaterialIcon name="cash" size={12} color={theme.textDim} />
                <NtText style={styles.currencyText}>
                  {currency.id} · {currency.symbol}
                </NtText>
              </View>
            ) : null}
          </View>
          {tariff.description ? (
            <NtText style={styles.description}>{tariff.description}</NtText>
          ) : null}
        </View>

        <View style={styles.statRow}>
          <NtCallingRatesStatItem
            icon="cash"
            tone="brand"
            title="Default Cost"
            value={`${symbol}${parseDecimalPlaces(tariff.default_cost)}`}
          />
          <NtCallingRatesStatItem
            icon="tag"
            tone="green"
            title="Default Price"
            value={`${symbol}${parseDecimalPlaces(tariff.default_price)}`}
          />
          <NtCallingRatesStatItem
            icon="clock-outline"
            tone="yellow"
            title="Free Minutes"
            showSlashMin={false}
            value={(tariff.initblock || 0) / 60}
          />
        </View>
      </View>
    </View>
  );
}

const makeHeroStyles = (t) =>
  StyleSheet.create({
    hero: {
      flexDirection: "row",
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radiusCard,
      overflow: "hidden",
    },
    accent: {
      width: 4,
      backgroundColor: t.brand,
    },
    heroBody: { flex: 1, padding: 18 },
    heroHeader: { marginBottom: 14 },
    heroIdRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    label: {
      fontSize: 11,
      fontWeight: "700",
      color: t.textDim,
      textTransform: "uppercase",
      letterSpacing: 0.8,
    },
    currencyChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 4,
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.borderSoft,
    },
    currencyText: { fontSize: 11, fontWeight: "600", color: t.text },
    description: {
      fontSize: 13,
      color: t.textDim,
      marginTop: 6,
      lineHeight: 18,
    },
    statRow: {
      flexDirection: "row",
      alignItems: "stretch",
      flexWrap: "wrap",
      gap: 12,
    },
  });

class CallingRateController extends BasePinController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    tariffData: [],
    totalItems: 0,
    searchText: "",
    searchFilter: null,
    selectedTab: null,
    page: 1,
  };

  column_location = "@callingrates_column_storage";
  addTariffModalRef = React.createRef();

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      callingratescolumndata.filter((each) => each.default === true),
    );
    this.handleTariffFetch();
  }


  handleTariffFetch = async () => {
    this.setState({ loading: true });
    const response = await fetchTariffs(null);
    Logger("Tariffs fetch response", response);
    if (response.ok) {
      const parsed = response.data?.data?.map((each) => ({
        title: each.id,
        id: each.id,
        default_cost: each?.default_cost,
        default_price: each?.default_price,
        description: each?.description,
        initblock: each?.initblock,
        currency: fetchCurrency(each.default_currency),
      }));
      this.setState({ tariffData: parsed || [], selectedTab: parsed?.[0] || null }, () =>
        this.handleDataFetch(),
      );
    }
    this.setState({ loading: false });
  };

  handleDataFetch = async () => {
    if (!this.state.selectedTab?.id) return;
    this.setState({ loading: true });
    const response = await fetchCallingRates(
      this.state.selectedTab?.id,
      this.state.page,
      settings.pageSizeLoad,
      this.state.searchText,
      this.state.searchFilter,
    );
    Logger("CallingRates fetch response", response);
    if (response.ok) {
      this.setState({
        data: response.data?.data || [],
        totalItems: response.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchCallingRates(
      this.state.selectedTab?.id,
      1,
      10000000,
      this.state.searchText,
      this.state.searchFilter,
    );
    return response.ok ? response.data.data : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "creationdate") return parseSimpleDisplayDate(item[column]);
    if (column === "status") return item[column] == 1 ? "Active" : "Inactive";
    if (column === "price" || column === "cost") {
      return `${fetchCurrency(item.currency)?.symbol}${parseDecimalPlaces(item[column])}`;
    }
    return item[column];
  };

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, () => this.handleDataFetch());
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  handleTabChange = (tab) => {
    if (tab?.id === this.state.selectedTab?.id) return;
    this.setState(
      { selectedTab: tab, searchText: "", data: [], page: 1 },
      () => this.handleDataFetch(),
    );
  };

  renderAddNewForm = () => (
    <NtModal ref={this.addTariffModalRef}>
      <NtCallingRatesTariffNewForm
        tariff={this.state.selectedTab?.id}
        onCancel={() => this.addTariffModalRef.current?.dismiss()}
        onCreate={() => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.addTariffModalRef.current?.dismiss();
          showToast("Success", "Successfully created a new special rate");
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtCallingRatesItem
      key={item?.id}
      item={item}
      columns={this.state.columns}
      onUpdate={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", "Successfully updated a special rate");
      }}
    />
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const selected = this.state.selectedTab;
    const count = this.state.totalItems || 0;
    const subtitle = `Per-tariff destination rates and overrides · ${count} rate${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <NtPageActionButton
        icon="plus"
        title="Add Special Rate"
        variant="primary"
        onPress={() => this.addTariffModalRef.current?.show()}
      />
    ) : null;

    const filters =
      this.state.tariffData?.length > 0 ? (
        <NtTabController
          options={this.state.tariffData}
          selection={selected}
          onPress={this.handleTabChange}
        />
      ) : null;

    const searchBar = (
      <NtSearchBar value={this.state.searchText} onChangeText={this.handleSearch} />
    );

    return (
      <NtRootPageContainer
        pageTitle="Calling Rates"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <TariffHero tariff={selected} />

        <NtPageSection
          title="Special Rates"
          filters={filters}
          search={searchBar}
          style={{ marginTop: 12 }}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: canEdit ? 35 : 0 }}
              columns={callingratescolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="call-rates"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default CallingRateController;
